'use client';

import type { Product } from 'services/generated/commerce-service/models/Product';
import type { Variant } from 'services/generated/commerce-service/models/Variant';
import { useTranslation } from '@whoop/i18n/lang/client';
import { useState } from 'react';
import type { CountryCode, Currency, Language } from '@whoop/i18n';
import { getRegionFromRequest } from '@whoop/i18n';
import Image from 'next/image';
import clsx from 'clsx';
import type { ShopAccessoryCategory } from 'services/generated/commerce-service/models/ShopAccessoryCategory';
import {
  getDisplayPriceFromAttributes,
  getFirstImageForVariant,
  getParentProductFromProductList,
  getPriceValues,
  getVariantColor,
  getWYWImage,
  hasInseamAttributes,
  hasSizeAttributes,
} from 'ui/utils/variantHelpers';
import {
  getColorVariantsWithSameAttributes,
  getInseamVariantsWithSameAttributes,
  getSizeVariantsWithSameAttributes,
} from 'ui/utils/productHelpers';
import { RadioSwatchesV2 } from 'ui/components/RadioSwatchesV2/RadioSwatchesV2';
import { getFormatters } from 'ui/hooks/formatter';
import Link from '@/components/Link';
import { ReviewStarRating } from 'ui/components/ReviewStarRating/ReviewStarRating';
import { useNavigation } from '@/hooks/useNavigation';

export interface ProductCardProps {
  products: Product[];
  productCategory: ShopAccessoryCategory;
  currency: Currency;
  language: Language;
  country: CountryCode;
  slug: string;
  initialVariant: Variant;
  addPlus?: boolean;
}

export default function ProductCard({
  products,
  productCategory,
  currency,
  language,
  country,
  slug,
  initialVariant,
  addPlus = false,
}: ProductCardProps): JSX.Element {
  const cardName = productCategory.name;
  const { t } = useTranslation('accessoriesPage');
  const { formatDisplayPrice } = getFormatters({
    currency,
    language,
    region: getRegionFromRequest(country),
  });
  const [selectedVariant, setSelectedVariant] =
    useState<Variant>(initialVariant);

  // selectedProduct contains information that is shared across variants. In this component,
  // we need the product name and the variants of that product. The variant contains the
  // product images, prices, inventory, and attributes.
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    getParentProductFromProductList(initialVariant, products)!,
  );
  const isWYWProduct = selectedProduct.product_type === 'whoop-your-way';

  const variantColor = getVariantColor(selectedVariant);
  const colorVariants = getColorVariantsWithSameAttributes(
    products,
    selectedVariant,
  );

  const handleColorChange = (variant: Variant) => {
    setSelectedVariant(variant);
    const newProduct = getParentProductFromProductList(variant, products);
    setSelectedProduct(newProduct ?? products[0]);
  };

  const { displayPrice, originalPrice, isDiscounted, percentSavings } =
    getPriceValues(selectedVariant);

  const linkHref = `products/${slug}?sku=${selectedVariant.sku}`;

  const { goTo } = useNavigation();

  return (
    <div className='flex w-[290px] flex-col'>
      <Link
        className='group relative flex h-[290px] w-full items-center justify-center rounded-3xl border-none bg-gray-400'
        href={linkHref}
      >
        <Image
          alt={selectedProduct.name}
          className={clsx(
            'max-h-[290px] max-w-full overflow-hidden rounded-md object-contain',
          )}
          fill
          onError={(event) => (event.currentTarget.style.display = 'none')}
          src={
            isWYWProduct
              ? getWYWImage(selectedVariant)
              : getFirstImageForVariant(selectedVariant)
          }
        />
      </Link>
      <div className='flex w-full flex-1 flex-col justify-between gap-3 pb-1'>
        <div>
          <Link
            className='text-md inline-block bg-none pt-1 text-left font-semibold hover:underline'
            href={linkHref}
          >
            {`${variantColor ?? ''} ${cardName}`}
          </Link>
          <div className='font-4 flex flex-row'>
            <div className='font-bold'>
              {isWYWProduct
                ? getDisplayPriceFromAttributes(selectedVariant, language)
                : `${formatDisplayPrice(displayPrice, {
                    roundUp: true,
                  })}${addPlus ? '+' : ''}`}
            </div>
            {isDiscounted ? (
              <>
                <div className='font-4 text-gray-a700 flex items-center pl-2 line-through'>
                  {formatDisplayPrice(originalPrice, { roundUp: true })}
                </div>
                {percentSavings ? (
                  <div className='text-blue-strain pl-2 font-semibold uppercase tracking-wide'>
                    {t('savePercent', { percent: percentSavings })}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        {productCategory.key ? (
          <ReviewStarRating
            showAverageRating={true}
            showArrow={false}
            productHandle={productCategory.key}
            onClick={() => goTo(linkHref)}
          />
        ) : null}
        {colorVariants.length > 1 ? (
          <RadioSwatchesV2
            onClick={handleColorChange}
            selectedSwatch={selectedVariant}
            swatchSize='small'
            swatchType='color'
            variants={colorVariants}
          />
        ) : null}
        {hasSizeAttributes(selectedVariant) ? (
          <RadioSwatchesV2
            onClick={setSelectedVariant}
            selectedSwatch={selectedVariant}
            swatchSize='small'
            swatchType='size'
            variants={getSizeVariantsWithSameAttributes(
              selectedProduct,
              selectedVariant,
            )}
          />
        ) : null}
        {hasInseamAttributes(selectedVariant) ? (
          <RadioSwatchesV2
            onClick={setSelectedVariant}
            selectedSwatch={selectedVariant}
            swatchSize='small'
            swatchType='inseam'
            variants={getInseamVariantsWithSameAttributes(
              selectedProduct,
              selectedVariant,
            )}
          />
        ) : null}
      </div>
    </div>
  );
}
